import { useId, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Send } from "lucide-react";
import { TOKENS } from '../ui/dashboard-primitives'
import { COPY } from '../../copy/firstRun'

import { CoachAvatar } from "./CoachAvatar";
import { HIT_AREA_FROM_36_BORDERED } from "./hitArea";
import { AMBER, CREAM } from "./tokens";

/**
 * CoachDock — the coach's global entry point.
 *
 * Mounted by Layout, so it is present on every page inside the app shell. It
 * replaces the permanent Coach item that used to sit on the rail: the coach is
 * now something you *say something to* from wherever you are, rather than a
 * destination you navigate to and then start typing.
 *
 * /coach is a Layout child too (the coach is a page in the shell, not an
 * overlay — Coach v3), so the dock guards itself on pathname rather than
 * relying on where it is mounted. The coach page has its own composer, which
 * is deliberately the *same bar* as this one; rendering both would stack two
 * identical composers on one page. Keeping the guard here rather than in
 * Layout means the invariant travels with the component and is testable
 * without rendering the whole shell.
 *
 * Submitting hands the text to /coach as route state rather than posting it
 * here. The coach page owns the session, the stream and the transcript; giving
 * the dock its own copy of any of that would mean two things to keep in sync.
 * The trade is latency: the coach page can send only once its session has
 * loaded, one round trip (two for an empty session, which fetches its opener).
 * The page shows the message as sent, with the coach's thinking dots, from the
 * first frame meanwhile (AICoach, Apple design review SH-1); before that the
 * words vanished into a spinner for the whole wait.
 *
 * **Without AI processing the dock shrinks to a link** (`aiOff`, #399, CS-D12; `R2-dashboard-web`):
 * "Open the coach", the mascot and a chevron in the dock's own surface. It is a link because the web's
 * sidebar has no Coach item, so the dock is the only way to the coach page; a field that cannot send
 * would only collect a message nobody can answer. The coach page says why and offers the way back
 * (CS-D11). The shell reads `aiOff` from `GET /api/me`; `unknown` is not off.
 *
 * A single-line <input>, deliberately: the dock is a doorway. Anything long
 * enough to want a second line belongs in the coach page's own composer, which
 * auto-grows.
 */

export interface CoachRouteState {
  draft?: string;
}

const PLACEHOLDER = "Message the coach…";
const COACH_PATH = "/coach";

// Solid, not --warm-dark: page content scrolls under the dock (fonts.css, --chrome-surface).
const CHROME_SURFACE = "var(--chrome-surface)";
const CREAM_HAIRLINE = TOKENS.creamBorder;

export function CoachDock({ aiOff = false }: { aiOff?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState("");
  const inputId = useId();

  // Hooks first, then the guard: an early return above useState would break
  // the rules of hooks the moment the route changes under a mounted dock.
  const onCoachPage = location.pathname === COACH_PATH;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const draft = value.trim();
    setValue("");
    // An empty submit is still a valid gesture — it means "open the coach".
    // Passing `state: null` rather than `{ draft: "" }` keeps the coach page's
    // consume-once logic from having to special-case the empty string.
    navigate(COACH_PATH, draft ? { state: { draft } satisfies CoachRouteState } : undefined);
  };

  if (onCoachPage) return null;

  if (aiOff) {
    return (
      <Link
        to={COACH_PATH}
        // `w-max`: an absolutely positioned box with only `left: 50%` is as wide as the half of the page
        // that is left, and the label wrapped onto two lines at 375 px (measured in a browser).
        className="coach-dock fixed left-1/2 z-40 inline-flex min-h-14 w-max max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-3 rounded-[28px] py-2.5 pl-2.5 pr-5 text-sm font-medium no-underline outline-none transition-transform duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber) active:scale-[0.98] sm:left-[calc(50%+48px)]"
        style={{
          bottom: "var(--coach-dock-bottom)",
          background: CHROME_SURFACE,
          border: `1px solid ${CREAM_HAIRLINE}`,
          boxShadow:
            "0 18px 48px -16px rgba(20, 14, 12, 0.6), inset 0 1px 0 " + TOKENS.creamHairline,
          color: CREAM,
          fontFamily: "var(--font-body)",
        }}
      >
        <CoachAvatar size={36} />
        {COPY.aiOffDockLink}
        <ChevronRight className="h-3.5 w-3.5" style={{ color: TOKENS.creamSoft }} aria-hidden />
      </Link>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      aria-label="Message the AI coach"
      className="coach-dock fixed left-3 right-3 z-40 flex items-center gap-3 rounded-[20px] px-3 py-2.5 sm:left-[calc(50%+48px)] sm:right-auto sm:w-[min(40rem,calc(100vw-160px))] sm:-translate-x-1/2"
      style={{
        bottom: "var(--coach-dock-bottom)",
        background: CHROME_SURFACE,
        border: `1px solid ${CREAM_HAIRLINE}`,
        boxShadow:
          "0 18px 48px -16px rgba(20, 14, 12, 0.6), inset 0 1px 0 " + TOKENS.creamHairline,
      }}
    >
      <CoachAvatar size={36} />

      <label htmlFor={inputId} className="sr-only">
        Message the coach
      </label>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={PLACEHOLDER}
        autoComplete="off"
        // 16px on touch: under 16px iOS Safari zooms the page on focus (INPUT_BASE in form-primitives).
        className="min-w-0 flex-1 bg-transparent text-base pointer-fine:text-sm outline-none"
        style={{
          color: CREAM,
          fontFamily: "var(--font-body)",
          // Placeholder colour is set in index.css (.coach-dock input::placeholder)
          // because ::placeholder cannot be reached from an inline style.
          caretColor: AMBER,
        }}
      />

      <button
        type="submit"
        aria-label="Send message to the coach"
        /* Outline, not a ring — the inline boxShadow below would swallow a
           box-shadow-based ring entirely. See the note on Composer's send
           button, which is the same control. */
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] transition-transform duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber) active:scale-[0.95] ${HIT_AREA_FROM_36_BORDERED}`}
        style={{
          background: AMBER,
          color: "#1c1816",
          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
          border: "1px solid rgba(232, 197, 160, 0.8)",
          boxShadow: "0 6px 18px -6px " + TOKENS.amberEmphasis,
        }}
      >
        <Send className="h-[15px] w-[15px]" strokeWidth={2.2} aria-hidden />
      </button>
    </form>
  );
}
